// ============================================================================
// ExerciseCard — one catalog tile. Shows the localized name, a one-line
// description, the modality + level badges and the primary muscles, with a
// favourite STAR toggle pinned top-right. The whole card opens the detail
// sheet (button), while the star intercepts its own click. Hover lifts the
// card; the star springs + emits a quick burst when pinned (reduced-motion
// aware). A coral hairline marks custom exercises.
// ============================================================================
import { memo } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Badge } from "../../components";
import { IconStar } from "../../components/icons";
import { cn } from "../../lib/cn";
import type { Exercise } from "../../api";
import type { ExLocale } from "./lib";
import {
  exerciseDescription,
  exerciseName,
  levelTone,
  modalityOf,
  modalityTone,
  primaryMuscles,
  titleCase,
} from "./lib";

interface ExerciseCardProps {
  exercise: Exercise;
  locale: ExLocale;
  favorite: boolean;
  favPending: boolean;
  onOpen: () => void;
  onToggleFavorite: () => void;
}

function ExerciseCardImpl({
  exercise,
  locale,
  favorite,
  favPending,
  onOpen,
  onToggleFavorite,
}: ExerciseCardProps) {
  const { t } = useTranslation("exercises");
  const reduce = useReducedMotion();

  const name = exerciseName(exercise, locale);
  const desc = exerciseDescription(exercise, locale);
  const modality = modalityOf(exercise);
  const muscles = primaryMuscles(exercise).slice(0, 3);
  const isCustom = exercise.is_custom === 1;

  return (
    <div
      className={cn(
        "group relative flex flex-col rounded-lg bg-surface p-5 text-left shadow-sm ring-1 ring-line",
        "transition-all duration-200 ease-kinetic hover:-translate-y-1 hover:shadow-md",
        isCustom && "ring-coral/40",
      )}
    >
      {/* Favourite toggle (pinned, intercepts its own click). */}
      <motion.button
        type="button"
        onClick={onToggleFavorite}
        disabled={favPending}
        whileTap={reduce ? undefined : { scale: 0.82 }}
        aria-pressed={favorite}
        aria-label={favorite ? t("card.unfavorite") : t("card.favorite")}
        className={cn(
          "absolute right-3 top-3 z-10 grid h-9 w-9 place-items-center rounded-pill transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt focus-visible:ring-offset-2 focus-visible:ring-offset-surface",
          favorite
            ? "text-coral"
            : "text-body-faint hover:bg-surface-sunken hover:text-body-soft",
        )}
      >
        <motion.span
          key={favorite ? "on" : "off"}
          initial={reduce ? false : { scale: 0.6, rotate: -18 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: "spring", stiffness: 520, damping: 18 }}
          className="inline-flex"
        >
          <IconStar filled={favorite} width={20} height={20} />
        </motion.span>
      </motion.button>

      {/* Clickable body opens the detail sheet. */}
      <button
        type="button"
        onClick={onOpen}
        className="flex flex-1 flex-col items-start gap-3 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt focus-visible:ring-offset-4 focus-visible:ring-offset-surface"
      >
        <div className="flex flex-wrap items-center gap-1.5 pr-8">
          <Badge tone={modalityTone(modality)} solid>
            {t(`values.modality.${modality}`, titleCase(modality))}
          </Badge>
          {exercise.level && (
            <Badge tone={levelTone(exercise.level)}>
              {t(`values.level.${exercise.level.toLowerCase()}`, titleCase(exercise.level))}
            </Badge>
          )}
          {isCustom && (
            <Badge tone="ember" dot>
              {t("card.custom")}
            </Badge>
          )}
        </div>

        <h3 className="font-display text-base font-bold leading-snug tracking-tight text-body">
          {name}
        </h3>

        {desc && (
          <p className="line-clamp-2 text-sm text-body-soft">{desc}</p>
        )}

        {muscles.length > 0 && (
          <div className="mt-auto flex flex-wrap gap-1.5 pt-1">
            {muscles.map((m) => (
              <span
                key={m}
                className="rounded-pill bg-surface-sunken px-2 py-0.5 text-xs font-bold text-body-muted"
              >
                {t(`values.muscle.${m.toLowerCase()}`, titleCase(m))}
              </span>
            ))}
          </div>
        )}
      </button>
    </div>
  );
}

export const ExerciseCard = memo(ExerciseCardImpl);
